import moment from 'moment';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useObjectVal } from 'react-firebase-hooks/database';
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table';
import {
  Button,
  Col,
  Collapse,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
} from 'reactstrap';
import { AbilityContext, Can } from '../../auth/Can';
import { DeleteIcon } from '../../components/Icons';
import { Loader } from '../../components/Loader';
import { ColumnsSelector } from '../../components/table/ColumnsSelector';
import { DefaultColumnFilter } from '../../components/table/DefaultFilter';
import { resetPageIndexOnFilterAction } from '../../components/table/resetPageIndexOnFilterAction';
import { logger } from '../../logging';
import { getDAMApp } from '../../services/firebase';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref } from '../../utils/firebase';
import { query, remove, update } from 'firebase/database';
import { Partner } from './types';
import { PartnerModal } from './PartnerModal';
import { PartnersTable } from './PartnersTable'; // This will be the actual table component
const R = require('ramda');

const pagesStyles = require('../pages.css');
const partnersStyles = require('./partners.css');
const iconStyles = require('../../components/icons.css');

const now = moment().unix();

export const PartnerTable = ({
  archived,
  errored,
  showAll,
}: {
  archived?: boolean;
  errored?: boolean;
  showAll?: boolean;
}) => {
  const damApp = getDAMApp();
  const functions = getFunctions(damApp);
  const damAuth = getAuth(damApp);
  const TABLE_NAME = 'partners';
  const [user, userLoading] = useAuthState(damAuth as any);
  const [activeTab, setActiveTab] = useState('enabled');

  const [disableModal, setDisableModal] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [partnerToDisable, setPartnerToDisable] = useState<Partner | null>(
    null
  );
  const toggleDisableModal = () => {
    setDisableModal(!disableModal);
    if (disableModal === false) {
      setPartnerToDisable(null);
    }
  };

  const [revokeModal, setRevokeModal] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [partnerToRevoke, setPartnerToRevoke] = useState<Partner | null>(null);
  const toggleRevokeModal = () => {
    setRevokeModal(!revokeModal);
    if (revokeModal === false) {
      setPartnerToRevoke(null);
    }
  };

  const partnersRef = ref('partners');

  const partnersExportFilenamePrefix = 'active';

  // For now, we'll just fetch all partners. Filtering can be added later if needed.
  const partnersQuery = partnersRef;

  const [partnersList, loading, error] = useObjectVal(partnersQuery as any);
  const [values, setValues] = useState<Partner[]>([]);
  const [valuesLoading, setValuesLoading] = useState(false);

  const [isColumnsSelectOpen, setIsColumnsSelectOpen] = useState(false);
  const uid = damAuth.currentUser?.uid; // Use optional chaining for safety
  const userColsRef = uid
    ? ref(`user_preferences/${uid}/columns/${TABLE_NAME}`)
    : null;
  const [userCols, userColsLoading, userColsError] = useObjectVal(
    userColsRef as any
  );

  const [partner, setPartner] = useState<Partner | null>(null);
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);

  const ability = useContext(AbilityContext);
  // Assuming a permission 'manage', 'partners_admin' will exist
  const isPartnersAdmin = ability.can('manage', 'partners');

  // when partnersList changes
  useEffect(() => {
    if (isPartnerModalOpen === true) {
      return;
    }

    let isMounted = true;
    if (partnersList === null) {
      if (isMounted) {
        setValuesLoading(true);
        setValues([]);
      }
    }

    if (partnersList) {
      const partners = Object.keys(partnersList).filter((k) => {
        const v = partnersList[k];
        // Basic filtering, can be expanded later
        return v;
      });

      if (isMounted) {
        setValuesLoading(true);

        const data = partners.map((k) => {
          const value = partnersList[k];
          const labels = { ...value };
          return { ...labels, key: k };
        });

        setValues(data);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [partnersList]);

  useEffect(() => {
    if (isPartnerModalOpen === false) {
      logger.debug(`partnerModal has been closed. now reload the values`);

      let isMounted = true;
      if (partnersList) {
        const partners = Object.keys(partnersList).filter((k) => {
          const v = partnersList[k];
          return v;
        });

        if (isMounted) {
          setValuesLoading(true);
          setValues(
            partners.map((k) => {
              const value = partnersList[k];
              const labels = { ...value };
              return { ...labels, key: k };
            })
          );
        }
      }

      return () => {
        isMounted = false;
      };
    }
  }, [isPartnerModalOpen]);

  useEffect(() => {
    setValuesLoading(false);
  }, [values]);

  const updateData = async (
    rowIndex: number,
    columnId: string,
    value: any,
    updateTableData: boolean,
    key: string
  ) => {
    const partnerRef = ref(`partners/${key}`);
    const updatedPartner: Partial<Partner> = {};
    updatedPartner[columnId] = value;
    updatedPartner['modified'] = now;
    try {
      // Assuming update function exists for firebase/database
      // await update(partnerRef, updatedPartner);
      logger.info(
        `[updateData] - updating partner: ${key} with ${columnId}: ${value}`
      );
    } catch (e) {
      logger.error('[updateMyData] - error updating partner row: ', e);
    }

    if (updateTableData) {
      logger.debug(`setting values (update)`);
      setValues((old) =>
        old.map((row, index) => {
          if (index === rowIndex) {
            return {
              ...old[rowIndex],
              ...updatedPartner,
            };
          }
          return row;
        })
      );
    }
  };

  const handleRevokeCredentials = async (partnerId: string) => {
    logger.debug(`[handleRevokeCredentials] partnerid: ${partnerId}`);
    try {
      const callable = httpsCallable(
        functions,
        'accountManager-callableRevokePartnerCredentials'
      );
      await callable({ partner_id: partnerId });
      // Optionally, update the UI to reflect revoked credentials
      logger.info(`Credentials revoked for partner: ${partnerId}`);
    } catch (e) {
      logger.error(
        `[handleRevokeCredentials] - error revoking credentials for partner id: ${partnerId} - ${e}`
      );
    }
  };

  const handleDisablePartner = (partner: Partner) => {
    logger.debug(`[handleDisablePartner] partner: ${JSON.stringify(partner)}`);
    setPartnerToDisable(partner);
    setDisableModal(true);
  };

  const disablePartner = async () => {
    setDisabling(true);
    const { key } = partnerToDisable;
    logger.debug(`[disablePartner] partnerid: ${key}`);

    await handleRevokeCredentials(key);

    const now = moment().unix();
    const disabledBy = { uid: user.uid, email: user.email };

    try {
      const partnerRef = ref(`partners/${key}`);
      await update(partnerRef, {
        enabled: false,
        modified: now,
        disabledAt: now,
        disabledBy,
      });
    } catch (e) {
      logger.error(
        `[disablePartner] - error disabling partner id: ${key} - ${e}`
      );
    }

    const data = [...values];
    const index = data.findIndex((p) => p.key === key);
    if (index > -1) {
      data[index] = {
        ...data[index],
        enabled: false,
        modified: now,
        disabledAt: now,
        disabledBy,
      };
    }
    logger.debug(`setting values (disable)`);
    setValues(data);
    setDisabling(false);
    setDisableModal(false);
    setPartnerToDisable(null);
  };

  const handleRevokeCredentialsClick = (partner: Partner) => {
    setPartnerToRevoke(partner);
    setRevokeModal(true);
  };

  const revokeCredentials = async () => {
    if (!partnerToRevoke) return;
    setRevoking(true);
    const { key } = partnerToRevoke;
    await handleRevokeCredentials(key);
    setRevoking(false);
    toggleRevokeModal();
  };

  const handleRowClick = (row, cell, e) => {
    logger.debug(
      `[handleRowClick] row: ${JSON.stringify(row.original)} cell: ${
        cell.column.id
      }`
    );
    if (cell.column.id === 'actions') {
      return;
    }

    setPartner(row.original);
    setIsPartnerModalOpen(true);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'key',
        id: 'id',
      },
      {
        Header: 'Name',
        accessor: 'name',
        id: 'name',
      },
      {
        Header: 'Description',
        accessor: 'description',
        id: 'description',
      },
      {
        Header: 'Contact Email',
        accessor: 'contact_email',
        id: 'contact_email',
      },
      {
        Header: 'Partner Type',
        accessor: 'partner_type',
        id: 'partner_type',
      },
      {
        Header: 'Enabled',
        accessor: (row) => (row.enabled === false ? 'False' : 'True'),
        id: 'enabled',
      },
      {
        Header: 'Disabled At',
        accessor: (row) => {
          if (row.disabledAt) {
            return moment.unix(row.disabledAt).format('YYYY-MM-DD HH:mm:ss');
          }
          return '';
        },
        id: 'disabledAt',
      },
      {
        Header: 'Disabled By',
        accessor: (row) => {
          if (row.disabledBy) {
            return row.disabledBy.email;
          }
          return '';
        },
        id: 'disabledBy',
      },
      {
        Header: 'Created By',
        accessor: (row: Partner) => {
          if (row.createdBy) {
            return row.createdBy.email;
          }
          return '';
        },
        id: 'createdBy',
      },
      {
        Header: 'Actions',
        id: 'actions',
        Cell: ({ row }) => {
          const partner = row.original;
          return (
            <div className={partnersStyles.actionsRow}>
              <Button
                className={partnersStyles.actionButton}
                title="Revoke Credentials"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click from firing
                  handleRevokeCredentialsClick(partner);
                }}
              >
                Revoke
              </Button>
              <Button
                className={partnersStyles.actionButton}
                title="Disable"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click from firing
                  handleDisablePartner(partner);
                }}
              >
                <DeleteIcon fill="white" class={iconStyles.tick} />
              </Button>
            </div>
          );
        },
      },
      {
        Header: 'Created',
        accessor: (row) => {
          if (row.created) {
            return moment.unix(row.created).format('YYYY-MM-DD HH:mm:ss');
          }

          return '';
        },
        id: 'created',
      },
      {
        Header: 'Modified',
        accessor: (row) => {
          if (row.modified) {
            return moment.unix(row.modified).format('YYYY-MM-DD HH:mm:ss');
          }

          return '';
        },
        id: 'modified',
      },
    ],
    []
  );

  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      // fuzzyText: fuzzyTextFilterFnExp, // Assuming similar filter as workflows
    }),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  // memoize values - the heavy lifting is now done in the above useEffect
  const records = React.useMemo(() => {
    return values.filter((p) => {
      if (activeTab === 'enabled') {
        return p.enabled !== false;
      }
      return p.enabled === false;
    });
  }, [values, activeTab]);

  let hiddenColumns: any = R.keys(
    R.pickBy((val, key) => val === false, userCols)
  );

  const tableData = useTable(
    {
      columns,
      data: records,
      filterTypes,
      defaultColumn,
      stateReducer: resetPageIndexOnFilterAction,
      autoResetFilters: false,
      autoResetPage: false,
      autoResetSortBy: false,
      initialState: {
        sortBy: [
          {
            id: 'created',
            desc: true,
          },
        ],
        pageSize: 20,
        pageIndex: 0,
      },
      autoResetHiddenColumns: false,
      updateData,
      handleRowClick,
    } as any,
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  ) as any;

  React.useEffect(() => {
    if (userCols) {
      // Only set hidden columns if userCols is available
      hiddenColumns = R.keys(R.pickBy((val, key) => val === false, userCols));
      tableData.setHiddenColumns(hiddenColumns);
    }
  }, [userCols]);

  React.useEffect(() => {
    if (tableData && tableData.gotoPage) {
      tableData.gotoPage(0);
    }
  }, [activeTab]);

  if (loading || valuesLoading || userColsLoading || userLoading) {
    return <Loader loading={true} />;
  }

  if (error) {
    return <div>{`Error: ${error.message}`}</div>;
  }

  if (userColsError) {
    return <div>Error loading cols</div>;
  }

  if (!user) {
    return <div>Error loading current user</div>;
  }

  const toggleColumnsSelect = () =>
    setIsColumnsSelectOpen(!isColumnsSelectOpen);
  const togglePartnerModal = () => setIsPartnerModalOpen(!isPartnerModalOpen);

  return (
    <div className={pagesStyles.fullHeight}>
      {values && (
        <div className={`${pagesStyles.noPadding} ${pagesStyles.fullHeight}`}>
          <Row className={pagesStyles.headerRow}>
            <Col sm={{ size: 12 }} className={pagesStyles.noPadding}>
              <Button
                onClick={togglePartnerModal}
                className={partnersStyles.topButtons}
              >
                Create New Partner
              </Button>
              <Button
                onClick={toggleColumnsSelect}
                className={partnersStyles.topButtons}
              >
                Columns
              </Button>
            </Col>
          </Row>
          <Row>
            <Col sm={8}>
              <Collapse isOpen={isColumnsSelectOpen}>
                <ColumnsSelector
                  columns={columns}
                  userCols={userCols}
                  tableName={TABLE_NAME}
                />
              </Collapse>
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <Nav tabs>
                <NavItem>
                  <NavLink
                    active={activeTab === 'enabled'}
                    onClick={() => setActiveTab('enabled')}
                  >
                    Enabled
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    active={activeTab === 'disabled'}
                    onClick={() => setActiveTab('disabled')}
                  >
                    Disabled
                  </NavLink>
                </NavItem>
              </Nav>
            </Col>
          </Row>
          <Row className={pagesStyles.fullHeight}>
            <Col sm={{ size: 12 }} className={pagesStyles.fullHeight}>
              <Can I="manage" a="partners">
                {' '}
                {/* Assuming 'partners' permission */}
                <PartnersTable {...tableData} />
              </Can>
            </Col>
          </Row>
          <Row>
            <Col sm={{ size: 12 }}>
              <Can I="manage" a="partners">
                <Modal
                  isOpen={disableModal}
                  toggle={(e) => toggleDisableModal()}
                >
                  <ModalHeader toggle={(e) => toggleDisableModal()}>
                    Please Confirm Disable
                  </ModalHeader>
                  <ModalBody>
                    {`Please confirm that you'd like to disable partner: ${
                      (partnerToDisable && partnerToDisable.name) || ''
                    }`}
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      color="primary"
                      onClick={() => disablePartner()}
                      disabled={disabling}
                    >
                      Confirm
                    </Button>{' '}
                    <Button
                      color="secondary"
                      onClick={(e) => toggleDisableModal()}
                      disabled={disabling}
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </Modal>
              </Can>
            </Col>
          </Row>
          <Row>
            <Col sm={{ size: 12 }}>
              <Can I="manage" a="partners">
                <Modal isOpen={revokeModal} toggle={(e) => toggleRevokeModal()}>
                  <ModalHeader toggle={(e) => toggleRevokeModal()}>
                    Please Confirm Revoke Credentials
                  </ModalHeader>
                  <ModalBody>
                    {`Please confirm that you'd like to revoke credentials for partner: ${
                      (partnerToRevoke && partnerToRevoke.name) || ''
                    }`}
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      color="primary"
                      onClick={() => revokeCredentials()}
                      disabled={revoking}
                    >
                      Confirm
                    </Button>{' '}
                    <Button
                      color="secondary"
                      onClick={(e) => toggleRevokeModal()}
                      disabled={revoking}
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </Modal>
              </Can>
            </Col>
          </Row>
        </div>
      )}
      <Can I="manage" a="partners">
        {isPartnerModalOpen && (
          <Row className={pagesStyles.fullHeight}>
            <Col sm={{ size: 12 }} className={pagesStyles.fullHeight}>
              <PartnerModal
                partner={partner}
                setPartner={setPartner}
                isOpen={isPartnerModalOpen}
                toggle={setIsPartnerModalOpen}
                user={user}
                isPartnersAdmin={isPartnersAdmin}
                partnersList={values}
              />
            </Col>
          </Row>
        )}
      </Can>
    </div>
  );
};
